<script setup>
import { computed, ref, useAttrs } from 'vue'
import { useFormFieldContext } from '@/composables/useFormFieldContext'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  id: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: null },
  density: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  /** Accessible name when no visible BaseFormField label is used (e.g. filter bars). */
  ariaLabel: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])
const attrs = useAttrs()
const inputEl = ref(null)
const { resolveId, resolveDescribedby, resolveInvalid, resolveDensity } = useFormFieldContext()

const resolvedId = computed(() => resolveId(props.id))
const resolvedInvalid = computed(() => resolveInvalid(props.invalid))
const resolvedDensity = computed(() => resolveDensity(props.density))
const describedby = computed(() => resolveDescribedby(attrs['aria-describedby']))

const inputClass = computed(() => [
  'form-input',
  resolvedDensity.value === 'compact' ? 'form-input--compact' : '',
  resolvedInvalid.value ? 'is-invalid' : '',
  attrs.class,
])

function onInput(e) {
  emit('update:modelValue', e.target.value)
}

defineExpose({
  el: inputEl,
  click: () => inputEl.value?.click(),
  focus: () => inputEl.value?.focus(),
})
</script>

<template>
  <input
    ref="inputEl"
    :id="resolvedId"
    :type="type"
    :value="type === 'file' ? undefined : modelValue"
    :disabled="disabled"
    :placeholder="placeholder"
    :class="inputClass"
    :aria-invalid="resolvedInvalid ? true : undefined"
    :aria-describedby="describedby"
    :aria-label="ariaLabel || undefined"
    v-bind="{ ...attrs, class: undefined }"
    @input="onInput"
  />
</template>
